import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  Typography,
} from "@mui/material";
import { Track, TRACK_DOCUMENT_TYPES } from "../../../../types";
import { useState } from "react";
import EditFieldModal from "@/modules/common/components/modals/edit-field-modal";
import z from "zod";
import { HospitalityListingSchema } from "@/modules/property-tracks/utils/listing";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { useAddHospitalityListing } from "@/modules/property-tracks/hooks/listings/add-hospitality-listing.hook";
import { useGetRoomTypes } from "@/modules/property-tracks/hooks/listings/get-room-types.hook";
import { showToast } from "@/modules/common/utils";
import { TOAST_IDS, ToastType } from "@/modules/common/types";
import HospitalityListingForm from "./form";
import { useTrackDocumentQuery } from "@/modules/property-tracks/hooks/track-document.hook";
import ImageSelector from "@/modules/common/components/input/image-selector";
import { useReadonlyContext } from "@/modules/common/context/read-only.context";

type HospitalityListingFormFields = z.infer<
  ReturnType<typeof HospitalityListingSchema>
>;

export default function AddHospitalityListing({ track }: { track: Track }) {
  const [modalOpen, setModalOpen] = useState(false);
  const [locationPromptOpen, setLocationPromptOpen] = useState(false);

  const hasLocation =
    track.property.latitude != null && track.property.longitude != null;

  const { data: images, isLoading: imagesLoading } = useTrackDocumentQuery({
    uid: track.uid,
    documentType: TRACK_DOCUMENT_TYPES.Media,
  });

  const { handleSubmit, reset, control } =
    useForm<HospitalityListingFormFields>({
      resolver: zodResolver(HospitalityListingSchema()),
      defaultValues: {
        isActive: true,
        name: `${track.property.propertyType?.name} - ${track.property.compound?.name}`,
        nameAr: `${track.property.propertyType?.nameAr} - ${track.property.compound?.nameAr}`,
        singleBedCount: 0,
        kingBedCount: 0,
        twinBedCount: 0,
        queenBedCount: 0,
        sofaBedCount: 0,
        exceptionalPrices: [],
      },
    });

  const { data: roomTypes } = useGetRoomTypes();
  const roomTypeOptions = (roomTypes ?? []).map((rt) => ({
    label: rt.name,
    value: String(rt.id),
  }));

  const mutation = useAddHospitalityListing();

  const onSubmit: SubmitHandler<HospitalityListingFormFields> = (data) => {
    mutation.mutate(
      {
        name: data.name,
        nameAr: data.nameAr || undefined,
        description: data.description || undefined,
        descriptionAr: data.descriptionAr || undefined,
        price: data.price,
        priceForExtraPerson: data.priceForExtraPerson || undefined,
        currencyCode: data.currencyCode.value as any,
        checkInTimeStart: data.checkInTimeStart?.value
          ? Number(data.checkInTimeStart.value)
          : undefined,
        checkInTimeEnd: data.checkInTimeEnd?.value
          ? Number(data.checkInTimeEnd.value)
          : undefined,
        checkOutTime: data.checkOutTime?.value
          ? Number(data.checkOutTime.value)
          : undefined,
        roomTypeId: data.roomType?.value
          ? Number(data.roomType.value)
          : undefined,
        minNights: data.minNights ?? undefined,
        maxNights: data.maxNights ?? undefined,
        airbnbOfficialListingMarkup:
          data.airbnbOfficialListingMarkup ?? undefined,
        bookingEngineMarkup: data.bookingEngineMarkup ?? undefined,
        isActive: data.isActive,
        isFeatured: data.isFeatured ?? undefined,
        propertyUid: track.property.uid,
        documentUids: data.documentUids,
        maxNumberOfGuests: data.maxNumberOfGuests ?? undefined,
        singleBedCount: data.singleBedCount ?? undefined,
        kingBedCount: data.kingBedCount ?? undefined,
        twinBedCount: data.twinBedCount ?? undefined,
        queenBedCount: data.queenBedCount ?? undefined,
        sofaBedCount: data.sofaBedCount ?? undefined,
        viewType: (data.viewType?.value as any) ?? undefined,
        isExtraBedAllowed: data.isExtraBedAllowed ?? false,
        isSmokingAllowed: data.isSmokingAllowed ?? false,
        isPetsAllowed: data.isPetsAllowed ?? false,
        isPartiesAllowed: data.isPartiesAllowed ?? false,
        instantBookable: data.instantBookable,
        exceptionalPrices: data.exceptionalPrices ?? [],
      },
      {
        onSuccess: () => {
          handleCloseModal();
          reset();
          showToast({
            type: ToastType.SUCCESS,
            message: "Hospitality listing created successfully",
            toastId: TOAST_IDS.ADD_LISTING_SUCCESS,
            options: {
              position: "bottom-left",
              autoClose: 3000,
            },
          });
        },
        onError: (error) => {
          showToast({
            type: ToastType.ERROR,
            message:
              error?.response?.data?.message ||
              "Unexpected error occurred. Please try again.",
            toastId: TOAST_IDS.ADD_LISTING_ERROR,
            options: {
              position: "bottom-left",
              autoClose: 3000,
            },
          });
        },
      },
    );
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    reset();
  };

  const handleModalOpen = () => {
    if (!hasLocation) {
      setLocationPromptOpen(true);
      return;
    }
    setModalOpen(true);
    reset();
  };

  const { readonly } = useReadonlyContext();

  return (
    <>
      <Button
        disabled={readonly}
        variant="contained"
        onClick={handleModalOpen}
        sx={(theme) => ({
          display: "flex",
          gap: theme.nawy.spacing.xs,
          alignItems: "center",
          backgroundColor: theme.nawy.colors.semantic.layer.accent,
          textTransform: "none",
          ...theme.nawy.typography.english.variants.body.md.semibold,
        })}
      >
        Add Hospitality Listing
      </Button>
      <EditFieldModal
        open={modalOpen}
        setOpen={setModalOpen}
        header="Add Hospitality Listing"
        width={580}
        handleConfirm={handleSubmit(onSubmit)}
        handleCloseModal={handleCloseModal}
        isLoading={mutation.isPending}
      >
        <HospitalityListingForm
          control={control}
          roomTypeOptions={roomTypeOptions}
        />

        <Controller
          control={control}
          name="documentUids"
          render={({ field, fieldState }) => (
            <Box>
              <Typography
                sx={(t) => t.nawy.typography.english.variants.body.md.semibold}
              >
                Listing Media
              </Typography>
              <Typography
                sx={(t) => t.nawy.typography.english.variants.body.xs.regular}
              >
                Select photos
              </Typography>
              <ImageSelector
                images={images ?? []}
                loading={imagesLoading}
                selectedImagesIds={field.value ?? []}
                setSelectedImagesIds={(docs: string[]) => field.onChange(docs)}
              />
              {fieldState.error && (
                <FormHelperText error>
                  {fieldState.error.message}
                </FormHelperText>
              )}
            </Box>
          )}
        />
      </EditFieldModal>

      <Dialog
        open={locationPromptOpen}
        onClose={() => setLocationPromptOpen(false)}
      >
        <DialogTitle>Location Required</DialogTitle>
        <DialogContent>
          <Typography>
            Please set the latitude and longitude in the Overview section before
            adding a hospitality listing.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLocationPromptOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
